import { useState } from "react";
import { Meter, State, TOOLS, VerdictChip, fmtMoney, microToUsd, useAsync } from "@authoxi/ui-shared";
import type { Api, Mandate } from "../api";

/** enforcing / shadow / revoked. **Shadow is a MANDATE property, not an agent one** — an agent
 *  is active or revoked; whether its verdicts bite is decided by the mandate's `enforcement`. */
function modeOf(status: string, mandates: Mandate[]): "enforcing" | "shadow" | "revoked" | "none" {
  if (status !== "active") return "revoked";
  const live = mandates.filter((m) => m.status === "active");
  if (!live.length) return "none";
  return live.every((m) => m.enforcement !== "deny") ? "shadow" : "enforcing";
}

export function Agents({ api }: { api: Api }) {
  const agents = useAsync(() => api.agents());
  const mandates = useAsync(() => api.mandates());
  const usage = useAsync(() => api.usage("day"));

  return (
    <>
      <div className="view-head">
        <span className="label">Agents</span>
        <h1>Your agents.</h1>
        <p>
          Each one holds its own key — we have never seen a private key of yours, which is why a
          breach of us cannot impersonate your fleet.
        </p>
      </div>

      <RegisterAgent api={api} onDone={() => void agents.reload()} />

      {agents.loading && <State kind="loading" title="Loading the roster…" />}
      {agents.error && <State kind="error" title="Could not load agents" detail={agents.error} />}
      {agents.data?.length === 0 && (
        <State
          kind="empty"
          title="No agents yet."
          detail="An agent registers itself with a key it generated — one SDK call, or the button above."
        />
      )}

      <div className="agents">
        {(agents.data ?? []).map((a) => {
          const mine = (mandates.data ?? []).filter((m) => m.subject_id === a.agent_id);
          const mode = modeOf(a.status, mine);
          const row = (usage.data?.rows ?? []).find(
            (r) => r.agent_id === a.agent_id && r.resource === "inference_usd",
          );
          return (
            <a className="agent" href={`/console/agents/${a.agent_id}`} key={a.agent_id} data-testid="agent-row">
              <span className="agent-avatar">{(a.name ?? a.agent_id).slice(0, 2)}</span>
              <span>
                <span className="agent-name">{a.name ?? a.agent_id}</span>
                <span className="agent-sub">
                  {a.principal_did && !a.principal_did.startsWith("did:authoxi:tenant:")
                    ? `acts for ${a.principal_did.replace("did:authoxi:user:", "")}`
                    : "no individual named"}
                </span>
                {row?.limit ? (
                  <span style={{ display: "block", marginTop: "var(--s3)", maxWidth: 220 }}>
                    <Meter used={row.used} limit={row.limit} />
                  </span>
                ) : null}
              </span>
              <span style={{ textAlign: "right" }}>
                {mode === "revoked" && <VerdictChip value="deny" dim />}
                {mode === "shadow" && <span className="pill">shadow</span>}
                {mode === "enforcing" && <VerdictChip value="allow" dim />}
                {mode === "none" && <span className="pill">metered only</span>}
                <div className="sm muted" style={{ marginTop: "var(--s2)" }}>
                  {row
                    ? row.limit
                      ? `${fmtMoney(microToUsd(row.used))} / ${fmtMoney(microToUsd(row.limit))}`
                      : `${fmtMoney(microToUsd(row.used))} · no cap`
                    : "—"}
                </div>
              </span>
            </a>
          );
        })}
      </div>
    </>
  );
}

/**
 * Register an agent — and hand over the key.
 *
 * The keypair is generated **in this browser** and only the public half is sent. That is the
 * property the whole security story rests on: authoxi never holds a private key, so a breach
 * of us cannot impersonate a fleet.
 *
 * Which means the private half has to reach the agent somehow, and the previous console simply
 * never handed it over — you got an agent you could not run. It is shown once, here, with the
 * same warning `/start` gives the tenant key, and the SDK one-liner is offered beside it
 * because generating in the agent's own process is the better path when you have the choice.
 */
function RegisterAgent({ api, onDone }: { api: Api; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [seed, setSeed] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div style={{ marginBottom: "var(--s5)" }}>
      <div className="row">
        <button className="btn btn-go" data-testid="new-agent" onClick={() => setOpen((o) => !o)}>
          Register an agent
        </button>
        <span className="sm muted">
          or <span className="mono">cp.create_agent("name")</span> — one call, and the key never
          leaves the agent's own process
        </span>
      </div>

      {open && !seed && (
        <div className="card pad" style={{ marginTop: "var(--s4)" }}>
          <form
            className="row"
            data-testid="register-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setErr(null);
              try {
                const { ed25519 } = await import("@noble/curves/ed25519");
                const { bytesToHex } = await import("@noble/hashes/utils");
                const { base64urlnopad } = await import("@scure/base");
                const priv = ed25519.utils.randomPrivateKey();
                const pub = ed25519.getPublicKey(priv);
                await api.registerAgent({
                  name: name.trim(),
                  public_key: base64urlnopad.encode(pub),
                });
                setSeed(bytesToHex(priv));
                onDone();
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            <input
              className="input"
              required
              placeholder="agent name"
              data-testid="agent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button className="btn btn-go" type="submit" disabled={busy}>
              {busy ? "…" : "Generate a key and register"}
            </button>
          </form>
          {err && <p className="sm" style={{ color: "var(--deny-text)" }}>{err}</p>}
        </div>
      )}

      {seed && (
        <div className="card pad" style={{ marginTop: "var(--s4)" }} data-testid="agent-seed">
          <p className="label" style={{ color: "var(--deny-text)" }}>
            The agent's private key — shown once
          </p>
          <pre className="code" style={{ marginTop: "var(--s3)", wordBreak: "break-all" }}>
            {seed}
          </pre>
          <p className="sm" style={{ color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
            Give this to the agent and to nothing else. We never received it and cannot recover
            it — if it is lost, revoke the agent and register a new one. Hex, which is what{" "}
            <span className="mono">key_to_seed()</span> returns and what every decoder in the
            SDK expects.
          </p>
          <button
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(seed);
              setSeed(null);
              setOpen(false);
              setName("");
            }}
          >
            Copy and close
          </button>
        </div>
      )}
    </div>
  );
}

export function AgentDetail({ api, agentId }: { api: Api; agentId: string }) {
  const agents = useAsync(() => api.agents());
  const mandates = useAsync(() => api.mandates({ subject_id: agentId }), [agentId]);
  const grants = useAsync(() => api.grants(agentId), [agentId]);
  const counters = useAsync(() => api.counters(agentId), [agentId]);
  const acted = useAsync(() => api.decisions({ agent_id: agentId, hours: 168, limit: 10 }), [agentId]);
  const [armed, setArmed] = useState(false);

  const agent = (agents.data ?? []).find((a) => a.agent_id === agentId);
  if (agents.loading) return <State kind="loading" title="Loading…" />;
  if (!agent) return <State kind="empty" title="No such agent." detail="It may have been revoked." />;

  const live = (mandates.data ?? []).filter((m) => m.status === "active");

  return (
    <>
      <a className="btn btn-quiet" href="/console/agents" style={{ marginBottom: "var(--s4)" }}>
        ← Agents
      </a>
      <div className="view-head">
        <span className="label">{agent.kind}</span>
        <h1>{agent.name ?? agent.agent_id}</h1>
        <p>
          {agent.principal_did && !agent.principal_did.startsWith("did:authoxi:tenant:")
            ? `Acts for ${agent.principal_did.replace("did:authoxi:user:", "")}. That binding is what makes “a human is accountable” a fact in a compliance pack rather than a claim.`
            : "No individual is named — the principal is the tenant itself. Nothing is wrong with that, but a compliance pack for this agent will say so plainly."}
        </p>
      </div>

      <div className="cards-2">
        <div className="card pad">
          <p className="label">What it may do</p>
          <div style={{ marginTop: "var(--s4)" }} className="stack">
            {live.length === 0 && (
              <p className="sm muted">
                Holds no money authority — like most agents. It is metered and recorded, and it
                cannot move money.
              </p>
            )}
            {live.map((m) => (
              <div key={m.mandate_id}>
                <div className="row">
                  <VerdictChip value="allow" dim />
                  <span className="mono sm">{m.allowed_actions.join(" · ") || "nothing"}</span>
                </div>
                {m.escalate_over && (
                  <div className="row" style={{ marginTop: "var(--s2)" }}>
                    <VerdictChip value="escalate" />
                    <span className="sm">over {fmtMoney(m.escalate_over, m.currency)}</span>
                  </div>
                )}
                <p className="xs muted" style={{ marginTop: "var(--s2)" }}>
                  {fmtMoney(m.spent, m.currency)} of {fmtMoney(m.budget, m.currency)} ·{" "}
                  enforcement <span className="mono">{m.enforcement}</span>
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="card pad">
          <p className="label">Identity</p>
          <dl className="kv" style={{ marginTop: "var(--s4)" }}>
            <dt>agent id</dt>
            <dd className="mono">{agent.agent_id}</dd>
            <dt>did</dt>
            <dd className="mono" style={{ wordBreak: "break-all" }}>
              {agent.agent_did}
            </dd>
            <dt>kind</dt>
            <dd>{agent.kind}</dd>
            <dt>public page</dt>
            <dd>
              <a href={`${TOOLS}/directory/agent/${agent.agent_id}`}>directory/agent/{agent.agent_id}</a>
            </dd>
          </dl>
          {counters.data && (
            <p className="xs muted" style={{ marginTop: "var(--s3)" }} data-testid="counters">
              {/* Not a vanity metric. This is the number the evidence store reconciles against:
                  a truncated tail in the record is otherwise indistinguishable from an ending. */}
              {counters.data.decisions_authorized} decisions authorized by this gate — the figure
              your evidence store reconciles its own count against.
            </p>
          )}
        </div>
      </div>

      {grants.data && grants.data.length > 0 && (
        <>
          <div className="rule" style={{ marginTop: "var(--s6)" }}>
            <span className="label">Keyring</span>
          </div>
          <div className="card pad">
            <div className="stack">
              {grants.data.map((g) => (
                <div className="between" key={g.grant_id}>
                  <span className="mono sm">{g.system}</span>
                  <span className="sm muted">{g.scopes.join(", ")}</span>
                  <VerdictChip value={g.status === "active" ? "allow" : "deny"} dim />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="rule" style={{ marginTop: "var(--s6)" }}>
        <span className="label">What it did</span>
      </div>
      {acted.data?.decisions.length === 0 && (
        <State kind="empty" title="Nothing in the last week." detail="A short working window, not the record." />
      )}
      <div className="timeline">
        {(acted.data?.decisions ?? []).map((d) => (
          <div className={`tl ${d.decision}`} key={d.observation_id}>
            <div className="tl-time">{d.created_at?.slice(5, 16) ?? ""}</div>
            <div className="tl-what">
              {d.action_type}
              {d.amount && ` · ${fmtMoney(d.amount, d.currency)}`}
            </div>
            <div className="tl-why">{d.reason_code ?? d.decision}</div>
          </div>
        ))}
      </div>

      {agent.status === "active" && (
        <div className="row" style={{ marginTop: "var(--s6)" }}>
          <button
            className="btn"
            data-testid="revoke"
            onClick={async () => {
              // Two-click: destructive, immediate, and cascading.
              if (!armed) return setArmed(true);
              await api.revokeAgent(agentId);
              await agents.reload();
              setArmed(false);
            }}
          >
            {armed ? "Confirm revoke" : "Revoke this agent"}
          </button>
          <span className="sm muted">
            Immediate. Its passport stops refreshing within one short TTL, and every sub-agent it
            spawned dies with it.
          </span>
        </div>
      )}
    </>
  );
}
